import { ReactElement, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import AuthContext from '../contexts/AuthContext';
import NavegacionContext, { NavegacionContextValue, PaginaReciente, PaginaVisitada } from '../contexts/NavegacionContext';
import { getClaveUsuario } from '../hooks/usePersistedState';

const MAX_RECIENTES = 12;
const CLAVE_RECIENTES = 'erp_recientes';
// Sin entrada en Recientes: el inicio ya tiene botón propio y "en construcción" no es una pantalla.
const NO_RECIENTES = ['/', '/en-construccion'];

interface EntradaPila {
    href: string;
    /** `location.key` del router; la primera página cargada no tiene. */
    key?: string;
}

const leerRecientes = (): PaginaReciente[] => {
    try {
        const guardado = JSON.parse(localStorage.getItem(getClaveUsuario(CLAVE_RECIENTES)) || '[]');
        return Array.isArray(guardado) ? guardado.filter((r) => r && typeof r.href === 'string').slice(0, MAX_RECIENTES) : [];
    } catch {
        return [];
    }
};

const guardarRecientes = (recientes: PaginaReciente[]): void => {
    try {
        localStorage.setItem(getClaveUsuario(CLAVE_RECIENTES), JSON.stringify(recientes));
    } catch {
        // Navegador sin almacenamiento (modo privado): Recientes dura solo la sesión.
    }
};

interface NavegacionStoreProps {
    children?: ReactNode;
}

/**
 * Historial de navegación dentro del ERP para el botón Volver y el panel
 * Recientes. La pila imita el historial de la pestaña: avanza con cada
 * navegación, retrocede cuando el usuario vuelve (botón Volver o el del
 * navegador). Al recargar empieza de cero y el botón Volver sube un nivel
 * en la ruta en vez de salir del ERP.
 */
const NavegacionStore = ({ children }: NavegacionStoreProps): ReactElement => {
    const { userRut } = useContext(AuthContext);
    const history = useHistory();
    const location = useLocation();
    const [pila, setPila] = useState<EntradaPila[]>([]);
    const [recientes, setRecientes] = useState<PaginaReciente[]>([]);
    // Títulos por URL. La ruta del sub-header los informa en un efecto que corre
    // antes que el de la pila (los efectos de los hijos van primero), así que
    // no pueden guardarse en la entrada de la pila.
    const titulos = useRef(new Map<string, Omit<PaginaVisitada, 'href'>>());
    const href = `${location.pathname}${location.search}`;

    useEffect(() => {
        setRecientes(userRut ? leerRecientes() : []);
    }, [userRut]);

    useEffect(() => {
        const entrada: EntradaPila = { href, key: location.key };
        const esMisma = (e?: EntradaPila): boolean =>
            !!e && (e.key && location.key ? e.key === location.key : e.href === href);

        setPila((prev) => {
            if (prev.length === 0) return [entrada];
            if (history.action === 'REPLACE') return [...prev.slice(0, -1), entrada];
            if (history.action === 'POP') {
                if (esMisma(prev[prev.length - 2])) return prev.slice(0, -1);
                if (esMisma(prev[prev.length - 1])) return prev;
            }
            return [...prev, entrada];
        });
        // Solo al cambiar de ubicación; `history.action` es el de ese cambio.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.key, href]);

    const recientesRef = useRef(recientes);
    recientesRef.current = recientes;

    const registrarTitulo = useCallback((hrefPagina: string, titulo: string, detalle?: string): void => {
        titulos.current.set(hrefPagina, { titulo, detalle });
        const ruta = hrefPagina.split('?')[0];
        if (!titulo || NO_RECIENTES.includes(ruta)) return;
        const actual = recientesRef.current;
        if (actual[0]?.href === hrefPagina && actual[0].titulo === titulo && actual[0].detalle === detalle) return;
        const nuevos = [{ href: hrefPagina, titulo, detalle, fecha: Date.now() }, ...actual.filter((r) => r.href !== hrefPagina)]
            .slice(0, MAX_RECIENTES);
        setRecientes(nuevos);
        guardarRecientes(nuevos);
    }, []);

    const borrarRecientes = useCallback((): void => {
        setRecientes([]);
        guardarRecientes([]);
    }, []);

    const anterior = pila.length > 1 ? pila[pila.length - 2] : undefined;

    const value: NavegacionContextValue = {
        anterior: anterior && { titulo: '', ...titulos.current.get(anterior.href), href: anterior.href },
        recientes,
        registrarTitulo,
        borrarRecientes,
    };

    return <NavegacionContext.Provider value={value}>{children}</NavegacionContext.Provider>;
};

export default NavegacionStore;
